import React from 'react';

import Image from '../../../../library/image/image';
import Typography from '../../../../library/typography/typography';
import CmsYoutubeVideo from '../../../../components/cms-youtube-video/cms-youtube-video';
import TreadwellPersonaIcon from '../../../../components/treadwell-persona-icon/treadwell-persona-icon';

import { GET_CMS_TREADWELL_TAB } from './graphql/treadwell-product-banner-queries';

import {
  useMediaQuery,
  useQuery,
  useTreadwellPreferences
} from '../../../../shared/hooks/hooks';

import { parseCmsContentAndMapComponents } from '../../../../shared/utils/cms/cms';

import { LowestPriceCoreProductData } from '../../../../shared/types/graphql-types';
import { PRODUCT_TYPES } from '../../../../shared/constants/products';

import './treadwell-product-banner.scss';

interface TreadwellProductBannerProps {
  lowestPriceCoreProductData?: LowestPriceCoreProductData;
  productCount: number;
  productType: keyof typeof PRODUCT_TYPES;
}

const componentMap = new Map([['youtube-video', CmsYoutubeVideo]]);

function TreadwellProductBanner(props: TreadwellProductBannerProps) {
  const { lowestPriceCoreProductData, productCount, productType } = props;
  const { treadwellPreferences } = useTreadwellPreferences();
  const { isMinLg } = useMediaQuery();
  const { data } = useQuery(GET_CMS_TREADWELL_TAB);
  const {
    selectedCustomDrivingPriorities,
    selectedDriverType,
    zipCodeWeatherData
  } = treadwellPreferences || {};
  const { icon, name } = selectedDriverType || {};
  const { city, state } = zipCodeWeatherData || {};

  const cmsSections = data?.cms?.page?.contentSlots || [];

  if (!treadwellPreferences) {
    return (
      <div styleName="cms-container">
        {cmsSections.map(section =>
          parseCmsContentAndMapComponents(
            section?.cmsComponents?.[0]?.html || '',
            componentMap
          )
        )}
      </div>
    );
  }

  return (
    <div styleName="container">
      {isMinLg && (
        <Image
          alt={productType}
          height="257"
          src={
            window.isDT
              ? 'https://discounttire.scene7.com/is/image/discounttire/plp-hero-asset_treadwell-dt?$PLP-hero$'
              : 'https://discounttire.scene7.com/is/image/discounttire/plp-hero-asset_treadwell-at?$PLP-hero$'
          }
          width="278"
        />
      )}
      <div styleName="description-container">
        <Typography styleName="title" tag="div" variant="H3">
          <Image
            alt="Treadwell"
            height="24px"
            src="https://discounttire.scene7.com/is/content/discounttire/logos/treadwell-icon-only-trademark.svg"
            styleName="treadwell-icon"
          />
          We found&nbsp;
          <strong>
            {productCount} perfect tire matches
            {!lowestPriceCoreProductData && ' for'}
          </strong>
          {lowestPriceCoreProductData && (
            <span>
              {', '}
              starting at <strong>${lowestPriceCoreProductData.value}</strong>
            </span>
          )}
        </Typography>
        <Typography tag="div" variant="H6">
          {!lowestPriceCoreProductData && 'a '}
          <strong>
            {name && <TreadwellPersonaIcon iconName={icon} />} {''}
            {name || 'Custom'}{' '}
          </strong>
          driver in{' '}
          <strong styleName="city">
            {city?.toLowerCase()}, {state}
          </strong>
        </Typography>
        <Typography size="extraSmall" styleName="size" tag="div">
          YOUR DRIVING PRIORITIES
        </Typography>
        <div styleName="priorities-container">
          {selectedDriverType
            ? selectedDriverType?.tooltips?.map(
                (priority, index) =>
                  index < 3 && (
                    <Typography
                      key={priority.id}
                      styleName="driving-priorities"
                      tag="div"
                    >
                      {`${index + 1}. ${priority.title}`}
                    </Typography>
                  )
              )
            : selectedCustomDrivingPriorities?.map(
                (drivingPriority, index) =>
                  index < 3 && (
                    <Typography
                      key={drivingPriority.id}
                      styleName="driving-priorities"
                      tag="div"
                    >
                      {index + 1}. {drivingPriority.name}
                    </Typography>
                  )
              )}
        </div>
      </div>
    </div>
  );
}

export default TreadwellProductBanner;
